import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { loadGoogleMaps } from "./PlacesInput";
import { MapPin, List, RefreshCw, Navigation, Phone, Car } from "lucide-react";

/**
 * Live Driver Map — dispatcher tab showing every driver's freshest GPS position
 * and their active trips (en route / on location / customer in car).
 * Polls /drivers/live every 10s. Map view + list view.
 */
const STATUS_META = {
  accepted:          { label: "Accepted",        color: "#60A5FA" },
  accepted_by_driver:{ label: "Accepted",        color: "#60A5FA" },
  assigned:          { label: "Assigned",        color: "#60A5FA" },
  en_route:          { label: "En route",        color: "#D4AF37" },
  on_location:       { label: "On location",     color: "#F59E0B" },
  customer_in_car:   { label: "Customer in car", color: "#10B981" },
};
const MKE = { lat: 43.0389, lng: -87.9065 };

function markerColor(d) {
  if (d.trips.length) return STATUS_META[d.trips[0].status]?.color || "#D4AF37";
  return d.online ? "#10B981" : "#71717A";
}
function agoLabel(sec) {
  if (sec == null) return "no GPS yet";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

export default function DriverLiveMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("map");
  const [onlyActive, setOnlyActive] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const infoRef = useRef(null);
  const fittedRef = useRef(false);

  const load = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const r = await api.get("/drivers/live");
      setData(r.data);
    } catch { /* keep last */ }
    finally { if (manual) setRefreshing(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const shown = (data?.drivers || []).filter(d => !onlyActive || d.online || d.trips.length);
  const located = shown.filter(d => d.location);

  // ── Map rendering ──────────────────────────────────────────────────────
  useEffect(() => {
    if (view !== "map" || !data) return;
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled || !mapElRef.current) return;
      if (!mapRef.current) {
        mapRef.current = new google.maps.Map(mapElRef.current, {
          center: MKE, zoom: 10, gestureHandling: "greedy",
          streetViewControl: false, mapTypeControl: true, fullscreenControl: true,
          styles: [
            { elementType: "geometry", stylers: [{ color: "#1d1d1d" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
            { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3a3a3a" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#0d1419" }] },
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
        infoRef.current = new google.maps.InfoWindow();
      }
      const map = mapRef.current;
      const keep = new Set();
      located.forEach(d => {
        keep.add(d.driver_id);
        const pos = { lat: d.location.lat, lng: d.location.lng };
        const color = markerColor(d);
        const icon = {
          path: google.maps.SymbolPath.CIRCLE, scale: 9,
          fillColor: color, fillOpacity: 1, strokeColor: "#0A0A0A", strokeWeight: 2.5,
        };
        let m = markersRef.current[d.driver_id];
        if (!m) {
          m = new google.maps.Marker({ map, position: pos, icon, title: d.name,
            label: { text: (d.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase(), color: "#0A0A0A", fontSize: "9px", fontWeight: "700" } });
          m.addListener("click", () => {
            const t = (markersRef.current[d.driver_id].__data || d);
            const trip = t.trips[0];
            infoRef.current.setContent(`
              <div style="font-family:Helvetica,Arial,sans-serif;color:#111;min-width:220px;padding:2px 4px;">
                <div style="font-weight:700;font-size:14px;">${t.name || "Driver"}</div>
                <div style="font-size:11px;color:#555;">${t.vehicle || ""}${t.phone ? " · " + t.phone : ""}</div>
                <div style="font-size:11px;color:#555;margin-top:2px;">GPS ${agoLabel(t.location?.age_seconds)}${t.location?.speed_mph ? ` · ${Math.round(t.location.speed_mph)} mph` : ""}</div>
                ${trip ? `
                  <div style="margin-top:6px;padding-top:6px;border-top:1px solid #eee;">
                    <div style="font-size:11px;font-weight:700;color:${STATUS_META[trip.status]?.color || "#111"};">${STATUS_META[trip.status]?.label || trip.status} · ${trip.confirmation_code || ""}</div>
                    <div style="font-size:11px;color:#333;margin-top:2px;">${trip.customer_name || ""}</div>
                    <div style="font-size:11px;color:#555;">${(trip.pickup_address || "").split(",")[0]} → ${(trip.dropoff_address || "").split(",")[0]}</div>
                    <a href="/dispatcher/reservation/${trip.booking_id}" style="font-size:11px;font-weight:700;color:#B8860B;">Open reservation →</a>
                  </div>` : `<div style="font-size:11px;color:#777;margin-top:4px;">No active trip</div>`}
              </div>`);
            infoRef.current.open({ map, anchor: markersRef.current[d.driver_id] });
          });
          markersRef.current[d.driver_id] = m;
        } else {
          m.setPosition(pos);
          m.setIcon(icon);
        }
        m.__data = d;
      });
      Object.entries(markersRef.current).forEach(([id, m]) => {
        if (!keep.has(id)) { m.setMap(null); delete markersRef.current[id]; }
      });
      if (!fittedRef.current && located.length) {
        const b = new google.maps.LatLngBounds();
        located.forEach(d => b.extend({ lat: d.location.lat, lng: d.location.lng }));
        map.fitBounds(b, 90);
        if (located.length === 1) map.setZoom(13);
        fittedRef.current = true;
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, data, onlyActive]);

  // Map instance dies with its DOM node when switching views
  useEffect(() => {
    if (view !== "map") { mapRef.current = null; markersRef.current = {}; fittedRef.current = false; }
  }, [view]);

  const s = data?.stats || { total: 0, online: 0, on_trip: 0 };

  return (
    <div data-testid="live-map-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2"><MapPin size={20} className="text-[#D4AF37]"/> Live driver map</h2>
          <p className="text-xs text-[#A1A1AA] mt-1">Real-time tracking · updates every 10 seconds while drivers share GPS on their trips</p>
        </div>
        <div className="flex items-center gap-6">
          {[["Total", s.total, "#F5F2EB"], ["Online", s.online, "#10B981"], ["On trip", s.on_trip, "#D4AF37"]].map(([l, n, c]) => (
            <div key={l} className="text-center" data-testid={`live-stat-${l.toLowerCase().replace(" ", "-")}`}>
              <div className="text-2xl font-serif" style={{ color: c }}>{n}</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#71717A]">{l}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <button data-testid="live-filter-toggle" onClick={() => { setOnlyActive(v => !v); fittedRef.current = false; }}
            className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] border ${onlyActive ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-white/15 text-[#A1A1AA]"}`}>
            {onlyActive ? "Active drivers" : "All drivers"}
          </button>
          <div className="flex border border-white/15">
            <button data-testid="live-view-map" onClick={() => setView("map")}
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${view === "map" ? "bg-[#D4AF37] text-[#0A0A0A] font-bold" : "text-[#A1A1AA] hover:text-white"}`}>
              <MapPin size={11}/> Map
            </button>
            <button data-testid="live-view-list" onClick={() => setView("list")}
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#D4AF37] text-[#0A0A0A] font-bold" : "text-[#A1A1AA] hover:text-white"}`}>
              <List size={11}/> List
            </button>
          </div>
          <button data-testid="live-refresh" onClick={() => load(true)}
            className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] inline-flex items-center gap-1.5">
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""}/> Refresh
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-5 mb-4 flex-wrap text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
        {[["#60A5FA", "Accepted"], ["#D4AF37", "En route"], ["#F59E0B", "On location"], ["#10B981", "In car / online idle"], ["#71717A", "Offline"]].map(([c, l]) => (
          <span key={l} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }}/>{l}</span>
        ))}
      </div>

      {view === "map" ? (
        <div className="relative border border-white/10">
          <div ref={mapElRef} data-testid="live-map-canvas" style={{ height: "62vh", width: "100%" }}/>
          {located.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-[#0A0A0A]/90 border border-white/10 px-6 py-4 text-center" data-testid="live-map-empty">
                <Navigation size={18} className="mx-auto text-[#71717A] mb-2"/>
                <div className="text-xs text-[#A1A1AA]">No live GPS right now</div>
                <div className="text-[10px] text-[#71717A] mt-1">Positions appear when a driver starts sharing location on a trip</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid="live-list">
          {shown.length === 0 && <div className="p-6 text-xs text-[#71717A]">No drivers match this filter.</div>}
          {shown.map(d => {
            const trip = d.trips[0];
            return (
              <div key={d.driver_id} data-testid={`live-row-${d.driver_id}`} className="flex flex-wrap items-center gap-4 px-4 py-3 border-b border-white/5 last:border-b-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: markerColor(d) }}/>
                <div className="min-w-[160px]">
                  <div className="text-sm text-[#F5F2EB]">{d.name}</div>
                  <div className="text-[10px] text-[#71717A] flex items-center gap-2">
                    {d.vehicle && <span className="inline-flex items-center gap-1"><Car size={9}/>{d.vehicle}</span>}
                    {d.phone && <span className="inline-flex items-center gap-1"><Phone size={9}/>{d.phone}</span>}
                  </div>
                </div>
                <div className="text-[10px] text-[#71717A] min-w-[90px]">GPS {agoLabel(d.location?.age_seconds)}</div>
                {trip ? (
                  <>
                    <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border" style={{ color: STATUS_META[trip.status]?.color, borderColor: `${STATUS_META[trip.status]?.color}55` }}>
                      {STATUS_META[trip.status]?.label || trip.status}
                    </span>
                    <div className="text-xs text-[#A1A1AA] flex-1 min-w-[220px] truncate">
                      <span className="text-[#D4AF37] font-mono mr-2">{trip.confirmation_code}</span>
                      {(trip.pickup_address || "").split(",")[0]} → {(trip.dropoff_address || "").split(",")[0]}
                    </div>
                    <button data-testid={`live-open-${trip.booking_id}`} onClick={() => navigate(`/dispatcher/reservation/${trip.booking_id}`)}
                      className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#F5F2EB]">Open →</button>
                  </>
                ) : (
                  <span className="text-[10px] text-[#71717A]">{d.online ? "Online · no active trip" : "Offline"}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
